import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { api } from "../api/client";
import type { SectorHeat, SectorTopTicker } from "../api/types";
import EmptyState from "../components/EmptyState";
import Footer from "../components/Footer";
import PageHeader from "../components/PageHeader";
import RatingBadge from "../components/RatingBadge";
import SectorTreemap from "../components/SectorTreemap";
import TopNav from "../components/TopNav";

type Stance = "buy" | "sell" | "neutral";

function useSectorHeat() {
  return useQuery<SectorHeat[]>({
    queryKey: ["sectors", "heat"],
    queryFn: async () => (await api.get<SectorHeat[]>("/sectors/heat")).data,
    refetchInterval: 5 * 60_000,
  });
}

function classifySector(score: number | null): Stance | null {
  if (score === null) return null;
  if (score >= 60) return "buy";
  if (score <= 40) return "sell";
  return "neutral";
}

function STANCE_META(stance: Stance) {
  switch (stance) {
    case "buy":
      return {
        title: "Buy / Strong Buy",
        accent: "border-emerald-500/30 bg-emerald-500/5",
        title_cls: "text-emerald-300",
        empty: "No bullish sectors today",
      };
    case "sell":
      return {
        title: "Sell / Strong Sell",
        accent: "border-rose-500/30 bg-rose-500/5",
        title_cls: "text-rose-300",
        empty: "No bearish sectors today",
      };
    case "neutral":
      return {
        title: "Neutral",
        accent: "border-slate-700 bg-slate-900",
        title_cls: "text-slate-300",
        empty: "No neutral sectors today",
      };
  }
}

function SectorRow({
  s,
  open,
  onToggle,
}: {
  s: SectorHeat;
  open: boolean;
  onToggle: () => void;
}) {
  const score = s.avg_composite_score;
  const dayChg = s.avg_day_change_pct;
  const dayCls =
    dayChg === null ? "text-slate-500" : dayChg >= 0 ? "text-emerald-400" : "text-rose-400";
  const w = score === null ? 0 : Math.max(2, Math.min(100, score));
  const barCls =
    score === null
      ? "bg-slate-700"
      : score >= 60
      ? "bg-emerald-500"
      : score <= 40
      ? "bg-rose-500"
      : "bg-slate-500";

  const drill = useQuery<SectorTopTicker[]>({
    queryKey: ["sector-top", s.sector],
    queryFn: async () =>
      (
        await api.get<SectorTopTicker[]>(
          `/sectors/${encodeURIComponent(s.sector)}/top`,
          { params: { stance: "all", limit: 10 } },
        )
      ).data,
    enabled: open,
    staleTime: 60_000,
  });

  return (
    <li className="px-3 py-2.5">
      <button
        onClick={onToggle}
        className="w-full text-left transition hover:opacity-90"
        aria-expanded={open}
      >
        <div className="flex items-baseline justify-between gap-2">
          <span className="text-sm font-medium text-slate-100 truncate">{s.sector}</span>
          <span className="shrink-0 text-[11px] tabular-nums text-slate-500">
            {score === null ? "—" : score.toFixed(0)}
          </span>
        </div>
        <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-slate-800">
          <div className={`h-full rounded-full ${barCls}`} style={{ width: `${w}%` }} />
        </div>
        <div className="mt-1 flex items-center justify-between text-[11px]">
          <span className="text-slate-500">{s.mention_count} mentions</span>
          <span className={`tabular-nums ${dayCls}`}>
            {dayChg === null ? "" : `${dayChg >= 0 ? "+" : ""}${dayChg.toFixed(2)}%`}
          </span>
        </div>
      </button>

      {open && (
        <div className="mt-2 rounded-md border border-slate-800 bg-slate-950/40 p-2">
          {drill.isLoading && (
            <div className="px-2 py-3 text-center text-[11px] text-slate-500">Loading…</div>
          )}
          {!drill.isLoading && (drill.data ?? []).length === 0 && (
            <div className="px-2 py-3 text-center text-[11px] text-slate-500">
              No scored tickers for this sector yet.
            </div>
          )}
          <ul className="divide-y divide-slate-800/70">
            {(drill.data ?? []).map((t) => {
              const dc = t.day_change_pct;
              const dcCls =
                dc === null ? "text-slate-500" : dc >= 0 ? "text-emerald-400" : "text-rose-400";
              return (
                <li key={t.yahoo_symbol} className="flex items-center gap-2 px-1.5 py-1.5">
                  <Link
                    to={`/s/${encodeURIComponent(t.yahoo_symbol)}`}
                    className="font-mono text-xs text-slate-100 hover:text-sky-300"
                  >
                    {t.ticker}
                  </Link>
                  <RatingBadge score={t.score} short />
                  <span className="ml-auto text-[11px] tabular-nums text-slate-500">
                    {t.last_close === null ? "—" : t.last_close.toFixed(2)}
                  </span>
                  <span className={`w-14 text-right text-[11px] tabular-nums ${dcCls}`}>
                    {dc === null ? "—" : `${dc >= 0 ? "+" : ""}${dc.toFixed(1)}%`}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </li>
  );
}

function SectorColumn({
  stance,
  items,
  openSector,
  setOpenSector,
}: {
  stance: Stance;
  items: SectorHeat[];
  openSector: string | null;
  setOpenSector: (s: string | null) => void;
}) {
  const meta = STANCE_META(stance);
  return (
    <section className={`overflow-hidden rounded-2xl border ${meta.accent}`}>
      <div className={`border-b border-slate-800 px-3 py-2 text-[11px] font-semibold uppercase tracking-wide ${meta.title_cls}`}>
        {meta.title}
        <span className="ml-2 text-slate-500">({items.length})</span>
      </div>
      <ul className="divide-y divide-slate-800/60">
        {items.length === 0 && (
          <li className="px-3 py-6 text-center text-[11px] text-slate-500">{meta.empty}</li>
        )}
        {items.map((s) => (
          <SectorRow
            key={s.sector}
            s={s}
            open={openSector === s.sector}
            onToggle={() => setOpenSector(openSector === s.sector ? null : s.sector)}
          />
        ))}
      </ul>
    </section>
  );
}

export default function Sectors() {
  const sectors = useSectorHeat();
  const items = sectors.data ?? [];
  const [openSector, setOpenSector] = useState<string | null>(null);

  const buy = items
    .filter((s) => classifySector(s.avg_composite_score) === "buy")
    .sort((a, b) => (b.avg_composite_score ?? 0) - (a.avg_composite_score ?? 0));
  const sell = items
    .filter((s) => classifySector(s.avg_composite_score) === "sell")
    .sort((a, b) => (a.avg_composite_score ?? 0) - (b.avg_composite_score ?? 0));
  const neutral = items
    .filter((s) => classifySector(s.avg_composite_score) === "neutral")
    .sort((a, b) => Math.abs((a.avg_composite_score ?? 50) - 50) - Math.abs((b.avg_composite_score ?? 50) - 50));

  return (
    <div className="mx-auto max-w-5xl p-4 sm:p-6">
      <PageHeader />
      <TopNav />

      <p className="mb-3 text-xs leading-relaxed text-slate-500 sm:mb-4">
        Daily aggregate per sector: news mentions, average composite score (0-100), and average day-change.
        Click a sector to drill into the top tickers driving the score. Hourly refresh.
      </p>

      {items.length > 0 && (
        <section className="mb-4 rounded-2xl border border-slate-800 bg-slate-900 p-3 sm:p-4">
          <div className="mb-2 text-xs uppercase tracking-wide text-slate-500">Sector rotation (today)</div>
          <SectorTreemap data={items} />
        </section>
      )}

      {sectors.isError && (
        <EmptyState
          variant="error"
          title="Couldn't load sector data"
          hint="The hourly cron may have failed or yfinance is unreachable."
          action={
            <button
              onClick={() => sectors.refetch()}
              className="rounded-lg border border-rose-800 px-3 py-1.5 text-xs text-rose-200 transition hover:bg-rose-900/40"
            >
              Retry
            </button>
          }
        />
      )}

      {!sectors.isError && sectors.isLoading && (
        <EmptyState variant="loading" title="Loading…" />
      )}

      {!sectors.isError && !sectors.isLoading && items.length === 0 && (
        <EmptyState
          title="No sector data yet"
          hint="Sectors are populated once symbols have a sector tag and the hourly cron runs."
        />
      )}

      {items.length > 0 && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <SectorColumn
            stance="buy"
            items={buy}
            openSector={openSector}
            setOpenSector={setOpenSector}
          />
          <SectorColumn
            stance="sell"
            items={sell}
            openSector={openSector}
            setOpenSector={setOpenSector}
          />
          <SectorColumn
            stance="neutral"
            items={neutral}
            openSector={openSector}
            setOpenSector={setOpenSector}
          />
        </div>
      )}

      <Footer note="Sector aggregates refresh hourly · Source: yfinance" />
    </div>
  );
}
